import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';

/**
 * Puerta del panel de administración.
 *
 * Las tablas usuarios y transacciones ya no son accesibles con la clave anon: solo
 * responden a una sesión real de Supabase Auth cuyo correo tenga plan ADMI. Este
 * componente abre esa sesión; sin ella el panel no puede leer ni escribir nada.
 */
// El motivo real importa: casi siempre es configuración del proyecto, no la contraseña.
const explicarFallo = (fallo) => {
  const codigo = fallo?.code || fallo?.error_code || '';
  if (codigo === 'email_provider_disabled') {
    return 'Supabase tiene desactivado el inicio de sesión por correo. Actívalo en Authentication > Sign In / Providers > Email.';
  }
  if (codigo === 'email_not_confirmed') {
    return 'La cuenta existe pero su correo no está confirmado. Confírmalo en Authentication > Users.';
  }
  if (codigo === 'invalid_credentials') {
    return 'Correo o contraseña incorrectos.';
  }
  return `No se pudo iniciar sesión: ${fallo?.message || 'error desconocido'}`;
};

export default function AdminAuth({ children }) {
  const [sesion, setSesion] = useState(null);
  const [revisando, setRevisando] = useState(true);
  const [correo, setCorreo] = useState('');
  const [clave, setClave] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let activo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!activo) return;
      setSesion(data?.session || null);
      setRevisando(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_evento, nueva) => {
      if (activo) setSesion(nueva);
    });
    return () => { activo = false; listener.subscription.unsubscribe(); };
  }, []);

  const entrar = async (evento) => {
    evento.preventDefault();
    setEnviando(true);
    setError('');
    const { error: fallo } = await supabase.auth.signInWithPassword({
      email: correo.trim().toLowerCase(),
      password: clave
    });
    if (fallo) setError(explicarFallo(fallo));
    setClave('');
    setEnviando(false);
  };

  const salir = async () => { await supabase.auth.signOut(); };

  if (revisando) return <p style={{ padding: 24 }}>Verificando sesión de administrador…</p>;

  if (!sesion) {
    return (
      <div style={{ maxWidth: 360, margin: '10vh auto', padding: 24, background: '#fff',
        border: '1px solid #e2e8f0', borderRadius: 12 }}>
        <h2 style={{ fontSize: '1.1rem', margin: '0 0 4px', color: '#0f172a' }}>Panel de administración</h2>
        <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '0 0 16px' }}>
          Entra con tu cuenta de administrador para ver y editar los datos.
        </p>
        <form onSubmit={entrar}>
          <label htmlFor="admin-correo" style={{ fontSize: '0.78rem', fontWeight: 600 }}>Correo</label>
          <input id="admin-correo" type="email" autoComplete="username" required value={correo}
            onChange={e => setCorreo(e.target.value)} disabled={enviando}
            style={{ width: '100%', padding: 9, margin: '4px 0 12px', border: '1px solid #cbd5e1', borderRadius: 8 }} />
          <label htmlFor="admin-clave" style={{ fontSize: '0.78rem', fontWeight: 600 }}>Contraseña</label>
          <input id="admin-clave" type="password" autoComplete="current-password" required value={clave}
            onChange={e => setClave(e.target.value)} disabled={enviando}
            style={{ width: '100%', padding: 9, margin: '4px 0 12px', border: '1px solid #cbd5e1', borderRadius: 8 }} />
          {error && <p role="alert" style={{ color: '#b42318', fontSize: '0.8rem' }}>{error}</p>}
          <button type="submit" disabled={enviando}
            style={{ width: '100%', padding: 10, border: 0, borderRadius: 8, background: '#0f766e',
              color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            {enviando ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
      </div>
    );
  }

  return <>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
      padding: '8px 16px', fontSize: '0.78rem', color: '#64748b' }}>
      <span>Administrador: {sesion.user?.email}</span>
      <button type="button" onClick={salir}
        style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8,
          padding: '4px 10px', cursor: 'pointer' }}>Cerrar sesión</button>
    </div>
    {children}
  </>;
}
